/**
 * KioskPinDialog — worker self-service kiosk PIN change (Jun 2026).
 * Opened from the profile menu. POST /api/users/me/kiosk-pin
 */
import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function KioskPinDialog({ open, onOpenChange }) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const clean = (v) => v.replace(/\D/g, '').slice(0, 4);

  const save = async () => {
    if (!/^\d{4}$/.test(pin)) {
      toast.error('PIN must be exactly 4 digits.');
      return;
    }
    if (pin !== confirm) {
      toast.error('PINs do not match.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/users/me/kiosk-pin`, { pin }, { headers: authHeaders() });
      toast.success('Your kiosk PIN has been updated.');
      setPin('');
      setConfirm('');
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update PIN');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-sm" data-testid="kiosk-pin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#1E88E5]" />
            <span>My Kiosk PIN</span>
          </DialogTitle>
          <DialogDescription>
            The 4-digit PIN you enter at the on-site kiosk to clock in and out.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>New PIN</label>
            <Input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(clean(e.target.value))}
              placeholder="••••"
              className="mt-1 text-center font-mono tracking-[0.5em]"
              data-testid="kiosk-pin-new-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>Confirm PIN</label>
            <Input
              type="password"
              inputMode="numeric"
              value={confirm}
              onChange={(e) => setConfirm(clean(e.target.value))}
              placeholder="••••"
              className="mt-1 text-center font-mono tracking-[0.5em]"
              data-testid="kiosk-pin-confirm-input"
            />
          </div>
          <Button
            onClick={save}
            disabled={busy || pin.length !== 4 || confirm.length !== 4}
            className="w-full bg-[#1E88E5] hover:bg-[#1976D2] text-white"
            data-testid="kiosk-pin-save-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save PIN
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
